import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { getItem, createRental, getImageUrl, getUserReviews, getItemCalendar, toggleWishlist, getWishlistIds, getSimilarItems, getRentalContract, getInsuranceOptions, getVerificationStatus } from '../services/api';
import StarRating from '../components/StarRating';
import ItemCard from '../components/ItemCard';
import { getCategoryPlaceholder } from '../utils/placeholders';
import { MapPin, Clock, Star, MessageCircle, ChevronLeft, ChevronRight, User, Shield, Check, Heart, CalendarDays, DollarSign, FileText, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, differenceInDays, differenceInHours, parseISO, eachDayOfInterval } from 'date-fns';

const ItemDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [item, setItem] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showRentalDialog, setShowRentalDialog] = useState(false);
  const [bookedDates, setBookedDates] = useState([]);
  const [wishlisted, setWishlisted] = useState(false);
  const [similarItems, setSimilarItems] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('rebook') === '1' && item && isAuthenticated && item.available && user?.id !== item.owner_id) {
      setShowRentalDialog(true);
      searchParams.delete('rebook');
      setSearchParams(searchParams, { replace: true });
    }
  }, [item, isAuthenticated, user, searchParams, setSearchParams]);

  // Rental form state
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [durationType, setDurationType] = useState('daily');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [contractAccepted, setContractAccepted] = useState(false);
  const [contractText, setContractText] = useState('');
  const [showContract, setShowContract] = useState(false);
  const [insuranceTier, setInsuranceTier] = useState('none');
  const [insuranceOptions, setInsuranceOptions] = useState({});
  const [idVerified, setIdVerified] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const itemData = await getItem(id);
        setItem(itemData);

        const [reviewsData, calendarData] = await Promise.all([
          getUserReviews(itemData.owner_id),
          getItemCalendar(id),
        ]);
        setReviews(reviewsData);

        // Parse booked date ranges into individual dates
        const dates = [];
        for (const range of calendarData.booked_ranges) {
          try {
            const start = parseISO(range.start);
            const end = parseISO(range.end);
            eachDayOfInterval({ start, end }).forEach((d) => dates.push(d));
          } catch (err) {
            console.error('Failed to parse date range:', err);
          }
        }
        setBookedDates(dates);

        // Check wishlist
        if (isAuthenticated) {
          try {
            const ids = await getWishlistIds();
            setWishlisted(ids.includes(id));
          } catch (err) {
            console.error('Failed to fetch wishlist:', err);
          }
        }

        // Fetch similar items
        try {
          const similar = await getSimilarItems(id, 4);
          setSimilarItems(similar);
        } catch (err) {
          console.error('Failed to fetch similar items:', err);
        }

        // Fetch contract, insurance, verification
        if (isAuthenticated) {
          try {
            const [contractData, insData, verData] = await Promise.all([
              getRentalContract(),
              getInsuranceOptions(),
              getVerificationStatus()
            ]);
            setContractText(contractData.contract);
            setInsuranceOptions(insData);
            setIdVerified(verData.id_verified);
          } catch (err) {
            console.error('Failed to fetch rental options:', err);
          }
        } else {
          try {
            const [contractData, insData] = await Promise.all([getRentalContract(), getInsuranceOptions()]);
            setContractText(contractData.contract);
            setInsuranceOptions(insData);
          } catch (err) {
            console.error('Failed to fetch insurance options:', err);
          }
        }
      } catch (error) {
        console.error('Failed to fetch item:', error);
        toast.error('Item not found');
        navigate('/browse');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWishlistToggle = async () => {
    if (!isAuthenticated) { toast.error('Please log in'); return; }
    try {
      const res = await toggleWishlist(item.id);
      setWishlisted(res.action === 'added');
      toast.success(res.action === 'added' ? 'Added to wishlist' : 'Removed from wishlist');
    } catch { toast.error('Failed'); }
  };

  const calculatePrice = () => {
    if (!startDate || !endDate || !item) return 0;
    if (durationType === 'hourly') {
      const hours = differenceInHours(endDate, startDate) || 1;
      return ((item.price_hourly || item.price_daily / 8) * hours).toFixed(2);
    } else if (durationType === 'weekly') {
      const weeks = differenceInDays(endDate, startDate) / 7 || 1;
      return ((item.price_weekly || item.price_daily * 5) * weeks).toFixed(2);
    } else {
      const days = differenceInDays(endDate, startDate) || 1;
      return (item.price_daily * days).toFixed(2);
    }
  };

  const handleRentalSubmit = async () => {
    if (!startDate || !endDate) { toast.error('Please select rental dates'); return; }
    if (!contractAccepted) { toast.error('Please accept the rental contract'); return; }
    if (!idVerified) { toast.error('Please verify your ID before renting'); return; }
    setSubmitting(true);
    try {
      await createRental({
        item_id: item.id,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        duration_type: durationType,
        message: message || undefined,
        contract_accepted: true,
        insurance_tier: insuranceTier === 'none' ? null : insuranceTier,
      });
      toast.success('Rental request sent!');
      setShowRentalDialog(false);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create rental request');
    } finally {
      setSubmitting(false);
    }
  };

  const isBookedDate = (date) => bookedDates.some((d) => d.toDateString() === date.toDateString());

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse text-[#596652]">Loading...</div>
      </div>
    );
  }
  if (!item) return null;

  const images = item.images?.length > 0
    ? item.images.map((img) => getImageUrl(img))
    : [getCategoryPlaceholder(item.category)];

  const isOwner = user?.id === item.owner_id;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Helmet>
        <title>{`${item.title} — Rent for $${item.price_daily}/day | NeighborBorrow`}</title>
        <meta name="description" content={`Rent ${item.title} in ${item.owner_neighborhood} for $${item.price_daily}/day. ${item.description?.slice(0, 120)}`} />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <Link to="/browse" className="flex items-center text-[#596652] hover:text-[#D97757] transition-colors" data-testid="back-to-browse">
            <ChevronLeft className="w-4 h-4 mr-1" />Back to Browse
          </Link>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className="aspect-[4/3] bg-white rounded-2xl overflow-hidden border border-[#EBE8DF] relative">
              <img src={images[currentImageIndex]} alt={item.title} className="w-full h-full object-cover" onError={(e) => { e.target.src = getCategoryPlaceholder(item.category); }} data-testid="item-main-image" />
              {/* Wishlist Heart */}
              <button onClick={handleWishlistToggle} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-sm" data-testid="item-detail-wishlist-toggle">
                <Heart className={`w-5 h-5 transition-colors ${wishlisted ? 'text-[#D97757] fill-[#D97757]' : 'text-[#596652]'}`} />
              </button>
            </div>
            {images.length > 1 && (
              <div className="flex items-center justify-center gap-2">
                <Button variant="outline" size="icon" onClick={() => setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))} className="rounded-full border-[#EBE8DF]" data-testid="prev-image-button">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex gap-2">
                  {images.map((_, idx) => (
                    <button key={idx} onClick={() => setCurrentImageIndex(idx)} className={`w-2 h-2 rounded-full transition-colors ${idx === currentImageIndex ? 'bg-[#D97757]' : 'bg-[#EBE8DF]'}`} data-testid={`image-dot-${idx}`} />
                  ))}
                </div>
                <Button variant="outline" size="icon" onClick={() => setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))} className="rounded-full border-[#EBE8DF]" data-testid="next-image-button">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Item Details */}
          <div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#8C9A76]/15 text-[#596652] mb-3">{item.category}</span>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#2C3329] mb-3" data-testid="item-title">{item.title}</h1>

            {/* Location & Rating */}
            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center text-[#596652]"><MapPin className="w-4 h-4 mr-1" /><span>{item.owner_neighborhood}</span></div>
              {item.owner_rating > 0 && (
                <div className="flex items-center gap-1"><Star className="w-4 h-4 text-[#F59E0B] fill-current" /><span className="font-medium text-[#2C3329]">{item.owner_rating.toFixed(1)}</span></div>
              )}
            </div>

            {/* Pricing */}
            <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6 mb-6">
              <div className="flex flex-wrap gap-6">
                {item.price_hourly && (
                  <div><span className="text-2xl font-heading font-semibold text-[#D97757]">${item.price_hourly}</span><span className="text-[#596652]">/hour</span></div>
                )}
                <div><span className="text-2xl font-heading font-semibold text-[#D97757]">${item.price_daily}</span><span className="text-[#596652]">/day</span></div>
                {item.price_weekly && (
                  <div><span className="text-2xl font-heading font-semibold text-[#D97757]">${item.price_weekly}</span><span className="text-[#596652]">/week</span></div>
                )}
              </div>
              {/* Security Deposit */}
              {item.security_deposit && item.security_deposit > 0 && (
                <div className="mt-4 pt-4 border-t border-[#EBE8DF] flex items-center gap-2" data-testid="security-deposit-display">
                  <DollarSign className="w-4 h-4 text-[#8C9A76]" />
                  <span className="text-[#596652] text-sm">Security deposit:</span>
                  <span className="font-semibold text-[#2C3329]">${item.security_deposit}</span>
                  <span className="text-[#596652] text-xs">(refundable)</span>
                </div>
              )}
            </div>

            {/* Condition */}
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-4 h-4 text-[#8C9A76]" />
              <span className="text-[#596652]">Condition:</span>
              <span className="font-medium text-[#2C3329] capitalize">{item.condition}</span>
            </div>

            {/* Availability */}
            <div className="flex items-center gap-2 mb-6">
              <Check className={`w-4 h-4 ${item.available ? 'text-[#4CAF50]' : 'text-[#E53935]'}`} />
              <span className={item.available ? 'text-[#4CAF50]' : 'text-[#E53935]'}>{item.available ? 'Available for rent' : 'Currently unavailable'}</span>
            </div>

            {/* Description */}
            <div className="mb-6">
              <h3 className="font-heading font-medium text-[#2C3329] mb-2">Description</h3>
              <p className="text-[#596652] whitespace-pre-line">{item.description}</p>
            </div>

            {/* Action Buttons */}
            {!isOwner && (
              <div className="flex gap-3">
                {isAuthenticated ? (
                  <>
                    <Dialog open={showRentalDialog} onOpenChange={setShowRentalDialog}>
                      <DialogTrigger asChild>
                        <Button className="flex-1 bg-[#D97757] text-white rounded-full hover:bg-[#C56547] py-3" disabled={!item.available} data-testid="rent-button">
                          <Clock className="w-4 h-4 mr-2" />Request to Rent
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg w-full h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[90vh] sm:rounded-lg overflow-y-auto">
                        <DialogHeader><DialogTitle className="font-heading text-xl">Request Rental</DialogTitle><DialogDescription className="sr-only">Choose rental dates, insurance, and send your request</DialogDescription></DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-[#2C3329] mb-2">Rental Duration Type</label>
                            <Select value={durationType} onValueChange={setDurationType}>
                              <SelectTrigger className="w-full border-[#EBE8DF] rounded-xl" data-testid="duration-type-select"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {item.price_hourly && <SelectItem value="hourly">Hourly</SelectItem>}
                                <SelectItem value="daily">Daily</SelectItem>
                                {item.price_weekly && <SelectItem value="weekly">Weekly</SelectItem>}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-[#2C3329] mb-2">Start Date</label>
                              <Calendar mode="single" selected={startDate} onSelect={(date) => { setStartDate(date); if (!endDate || date > endDate) setEndDate(addDays(date, 1)); }} disabled={(date) => date < new Date() || isBookedDate(date)} className="rounded-xl border border-[#EBE8DF]" data-testid="start-date-calendar" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-[#2C3329] mb-2">End Date</label>
                              <Calendar mode="single" selected={endDate} onSelect={setEndDate} disabled={(date) => date < (startDate || new Date()) || isBookedDate(date)} className="rounded-xl border border-[#EBE8DF]" data-testid="end-date-calendar" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-[#2C3329] mb-2">Message to Owner (optional)</label>
                            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Hi! I'd like to rent this item..." className="border-[#EBE8DF] rounded-xl" rows={3} data-testid="rental-message" />
                          </div>

                          {/* Insurance Selection */}
                          <div>
                            <label className="block text-sm font-medium text-[#2C3329] mb-2">
                              <ShieldCheck className="w-4 h-4 inline mr-1" />
                              Rental Protection
                            </label>
                            <div className="space-y-2">
                              {Object.entries(insuranceOptions).map(([key, opt]) => (
                                <label
                                  key={key}
                                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                                    insuranceTier === key ? 'border-[#D97757] bg-[#D97757]/5' : 'border-[#EBE8DF] hover:bg-[#FDFBF7]'
                                  }`}
                                  data-testid={`insurance-${key}`}
                                >
                                  <input
                                    type="radio"
                                    name="insurance"
                                    value={key}
                                    checked={insuranceTier === key}
                                    onChange={() => setInsuranceTier(key)}
                                    className="accent-[#D97757]"
                                  />
                                  <div className="flex-1">
                                    <span className="font-medium text-[#2C3329] text-sm">{opt.name}</span>
                                    <p className="text-xs text-[#596652]">{opt.description}</p>
                                  </div>
                                  {opt.fee_percent > 0 && (
                                    <span className="text-xs font-medium text-[#D97757]">+{opt.fee_percent}%</span>
                                  )}
                                </label>
                              ))}
                            </div>
                          </div>

                          {/* Price Summary */}
                          {startDate && endDate && (
                            <div className="bg-[#FDFBF7] rounded-xl p-4 space-y-2">
                              <div className="flex justify-between items-center">
                                <span className="text-[#596652]">{format(startDate, 'MMM d')} - {format(endDate, 'MMM d, yyyy')}</span>
                                <span className="font-heading font-semibold text-[#2C3329]">${calculatePrice()}</span>
                              </div>
                              <div className="flex justify-between items-center text-sm text-[#596652]">
                                <span>+ Service fee (10%)</span>
                                <span className="font-medium text-[#2C3329]">${(parseFloat(calculatePrice()) * 0.1).toFixed(2)}</span>
                              </div>
                              {insuranceTier !== 'none' && insuranceOptions[insuranceTier] && (
                                <div className="flex justify-between items-center text-sm text-[#596652]">
                                  <span>+ {insuranceOptions[insuranceTier].name} ({insuranceOptions[insuranceTier].fee_percent}%)</span>
                                  <span className="font-medium text-[#2C3329]">${(parseFloat(calculatePrice()) * insuranceOptions[insuranceTier].fee_percent / 100).toFixed(2)}</span>
                                </div>
                              )}
                              {item.security_deposit > 0 && (
                                <div className="flex justify-between items-center text-sm text-[#596652]">
                                  <span>+ Refundable deposit</span>
                                  <span className="font-medium text-[#2C3329]">${item.security_deposit}</span>
                                </div>
                              )}
                              <div className="border-t border-[#EBE8DF] pt-2 flex justify-between items-center">
                                <span className="font-medium text-[#2C3329]">Total</span>
                                <span className="text-xl font-heading font-semibold text-[#D97757]">
                                  ${(parseFloat(calculatePrice()) * (1.1 + (insuranceTier !== 'none' && insuranceOptions[insuranceTier] ? insuranceOptions[insuranceTier].fee_percent / 100 : 0)) + (item.security_deposit || 0)).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* ID Verification Warning */}
                          {!idVerified && (
                            <div className="flex items-start gap-3 p-3 bg-[#F59E0B]/10 rounded-xl" data-testid="id-verification-warning">
                              <AlertTriangle className="w-5 h-5 text-[#F59E0B] shrink-0 mt-0.5" />
                              <div>
                                <p className="text-sm font-medium text-[#2C3329]">ID Verification Required</p>
                                <p className="text-xs text-[#596652]">You must verify your identity before renting.</p>
                                <Link to="/profile" className="text-xs text-[#D97757] hover:underline">Verify now</Link>
                              </div>
                            </div>
                          )}

                          {/* Contract */}
                          <div className="border border-[#EBE8DF] rounded-xl overflow-hidden">
                            <button
                              type="button"
                              onClick={() => setShowContract(!showContract)}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#FDFBF7] transition-colors"
                              data-testid="toggle-contract"
                            >
                              <span className="flex items-center gap-2 text-sm font-medium text-[#2C3329]">
                                <FileText className="w-4 h-4 text-[#8C9A76]" />
                                Rental Agreement
                              </span>
                              <span className="text-xs text-[#596652]">{showContract ? 'Hide' : 'View'}</span>
                            </button>
                            {showContract && (
                              <div className="px-3 pb-3 max-h-40 overflow-y-auto text-xs text-[#596652] whitespace-pre-line border-t border-[#EBE8DF]" data-testid="contract-text">
                                {contractText}
                              </div>
                            )}
                            <label className="flex items-center gap-2 p-3 border-t border-[#EBE8DF] cursor-pointer">
                              <input
                                type="checkbox"
                                checked={contractAccepted}
                                onChange={(e) => setContractAccepted(e.target.checked)}
                                className="accent-[#D97757] w-4 h-4"
                                data-testid="contract-checkbox"
                              />
                              <span className="text-sm text-[#2C3329]">I accept the rental agreement</span>
                            </label>
                          </div>

                          <Button onClick={handleRentalSubmit} disabled={!startDate || !endDate || !contractAccepted || !idVerified || submitting} className="w-full bg-[#D97757] text-white rounded-full hover:bg-[#C56547] py-3" data-testid="submit-rental-button">
                            {submitting ? 'Sending Request...' : 'Send Rental Request'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Link to={`/messages?user=${item.owner_id}`}>
                      <Button variant="outline" className="border-[#EBE8DF] rounded-full px-6 py-3" data-testid="message-owner-button">
                        <MessageCircle className="w-4 h-4 mr-2" />Message
                      </Button>
                    </Link>
                  </>
                ) : (
                  <Link to="/login" className="flex-1">
                    <Button className="w-full bg-[#D97757] text-white rounded-full hover:bg-[#C56547] py-3" data-testid="login-to-rent-button">Log in to Rent</Button>
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Availability Calendar + Owner + Reviews */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Availability Calendar */}
          <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6" data-testid="availability-calendar-section">
            <h3 className="font-heading font-semibold text-lg text-[#2C3329] mb-4 flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#D97757]" />Availability
            </h3>
            <Calendar
              mode="single"
              disabled={(date) => date < new Date() || isBookedDate(date)}
              modifiers={{ booked: bookedDates }}
              modifiersStyles={{ booked: { backgroundColor: '#FECACA', color: '#E53935', borderRadius: '50%' } }}
              className="rounded-xl border border-[#EBE8DF]"
              data-testid="item-availability-calendar"
            />
            <div className="flex items-center gap-4 mt-3 text-xs text-[#596652]">
              <div className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#FECACA] border border-[#E53935]/30" />Booked</div>
              <div className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-white border border-[#EBE8DF]" />Available</div>
            </div>
          </div>

          {/* Owner Info */}
          <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
            <h3 className="font-heading font-semibold text-lg text-[#2C3329] mb-4">About the Owner</h3>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#8C9A76] rounded-full flex items-center justify-center"><User className="w-6 h-6 text-white" /></div>
              <div>
                <p className="font-medium text-[#2C3329]">{item.owner_name}</p>
                <div className="flex items-center gap-1 text-sm text-[#596652]"><MapPin className="w-3 h-3" />{item.owner_neighborhood}</div>
              </div>
            </div>
            {item.owner_rating > 0 && (
              <div className="flex items-center gap-2">
                <StarRating rating={item.owner_rating} readonly size="sm" />
                <span className="text-[#596652] text-sm">({reviews.length} reviews)</span>
              </div>
            )}
          </div>

          {/* Reviews */}
          <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
            <h3 className="font-heading font-semibold text-lg text-[#2C3329] mb-4">Reviews</h3>
            {reviews.length > 0 ? (
              <div className="space-y-4">
                {reviews.slice(0, 5).map((review) => (
                  <div key={review.id} className="border-b border-[#EBE8DF] last:border-0 pb-4 last:pb-0">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-[#2C3329]">{review.reviewer_name}</span>
                      <StarRating rating={review.rating} readonly size="sm" />
                    </div>
                    <p className="text-[#596652] text-sm">{review.comment}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#596652]">No reviews yet</p>
            )}
          </div>
        </div>

        {/* Similar Items */}
        {similarItems.length > 0 && (
          <div className="mt-12" data-testid="similar-items-section">
            <h3 className="font-heading font-semibold text-xl text-[#2C3329] mb-6">Similar Items Nearby</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {similarItems.map((si) => (
                <ItemCard key={si.id} item={si} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemDetailPage;
